import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useResizeObserver } from '../../hooks/useResizeObserver';

const chooseFontSize = (fontSize: string, sizeRatio: number, continuousSizing: boolean) => {
  const safetyFactorPx = 0.98;
  const safetyFactorRaw = 0.9;

  const reMatch = /([0-9\.]*)(.*)/.exec(fontSize);
  const value = Number.parseFloat(reMatch?.[1] || '0');
  const unit = reMatch?.[2] || 'px';

  if (unit === 'px') {
    let pxSize = value * (safetyFactorPx * sizeRatio);

    if (!continuousSizing) {
      // If the unit is px, choose the closest allowable size below the calculated size
      const allowedSizes = [
        1024, 896, 768, 640, 512, 448, 384, 320, 256, 224, 192, 160, 128, 112, 96, 80, 64, 56, 48,
        40, 32, 28, 24, 20, 16, 14, 12, 10, 8, 7, 6, 5, 4, 3.5, 3, 2.5, 2, 1.5, 1,
      ];
      pxSize = allowedSizes.find((size) => size <= pxSize) ?? pxSize;
    }

    return pxSize + 'px';
  }

  const rawSize = value * (safetyFactorRaw * sizeRatio);
  return `${rawSize}${unit}`;
};

const AutoSizedText = ({
  children,
  elementType = 'p',
  continuousSizing = false,
  completelyFillWidth = false,
  className = '',
  style = undefined,
}: {
  children: React.ReactNode;
  elementType?: string;
  continuousSizing?: boolean;
  completelyFillWidth?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const textRef = useRef<HTMLElement>(null);
  const measurementRef = useRef<HTMLElement>(null);

  const [masterRect, setMasterRect] = useState<DOMRect | undefined>();
  const [measuredSize, setMeasuredSize] = useState<{ width: number; height: number }>({
    width: -1,
    height: -1,
  });

  const [fullFontSize, setFullFontSize] = useState<string | undefined>();

  useResizeObserver(textRef, (width, height) => {
    setMasterRect(textRef.current?.getBoundingClientRect());
  });

  useEffect(() => {
    const textElement = measurementRef.current;
    if (textElement) {
      const styleMap = globalThis.getComputedStyle(textElement);

      setFullFontSize(styleMap.fontSize);
      setMeasuredSize({ width: textElement.scrollWidth, height: textElement.scrollHeight });
    }
  }, [children, className, masterRect?.width, masterRect?.height]);

  const width = textRef.current?.clientWidth ?? 0;
  const height = textRef.current?.clientHeight ?? 0;

  let fontSize: string | undefined = undefined;
  if (measuredSize.width > 0 && measuredSize.height > 0 && fullFontSize) {
    const sizeRatio = width / Math.max(measuredSize.width, 1);
    if (sizeRatio < 1) {
      fontSize = chooseFontSize(fullFontSize, sizeRatio, continuousSizing);
    }
  }
  // Hide the text until it's measured
  if (measuredSize.width < 0) {
    fontSize = '0';
  }

  const useJustify = completelyFillWidth && fontSize;

  return (
    <>
      {React.createElement(elementType, {
        children,
        ref: textRef,
        className: clsx(className, 'whitespace-nowrap overflow-visible'),
        style: {
          ...style,
          fontSize: fontSize ?? style?.fontSize,
          textAlign: useJustify ? 'justify' : style?.textAlign,
          textAlignLast: useJustify ? 'justify' : style?.textAlignLast,
        },
      })}
      <div className="w-0 h-0 absolute overflow-hidden">
        {React.createElement(elementType, {
          children,
          ref: measurementRef,
          className: clsx(className, 'whitespace-nowrap overflow-hidden invisible'),
          style: {
            ...style,
            width: '0',
            overflow: 'visible',
            ariaHidden: true,
          },
        })}
      </div>
    </>
  );
};
export default AutoSizedText;
